import { EVENT_NAME as SYNC_STATE_CHANGE_EVENT } from '@adobe-fonts/site/events/SyncStateChangeEvent.js';

import { getService, scopeApply } from '../../util/angular_helpers.js';
import { syncVariations, unsyncVariations } from '../../util/Sync.js';
import { addPageAction } from '../../util/new_relic.js';
import { getData } from '../../util/script_data.js';

export class LibrariesToolbarSync {
  constructor(element) {
    this.element = element;
  }

  handleSyncStateChangeEvent = (event) => {
    const toolbar = event.target.closest('af-libraries-toolbar');
    if (!toolbar) {
      return;
    }

    // Initialize state so that other functions can access while handling this event
    this.toolbar = toolbar;
    this.tkFontIds = this._getTkFontIds(toolbar);
    this.ids = this.tkFontIds.map(this._toFontVariationId);

    const dataAnalytics = {
      fontVariations: this.ids
    };

    if (this.toolbar.synced) {
      addPageAction('typekit.click.libraries-toolbar-unsync-button', dataAnalytics);
      this.unsyncVariations();
    } else {
      addPageAction('typekit.click.libraries-toolbar-sync-button', dataAnalytics);
      this.syncVariations();
    }
  };

  syncVariations = async () => {
    try {
      await this.authenticateSyncVariations(this.ids);
      this.toolbar.synced = true;
      this.showSyncSuccessMessage(this.tkFontIds[0]);
      this.updateVariationCards(this.ids, true);
    } catch (e) {
      this.showSyncErrorMessage();
    }
  };

  authenticateSyncVariations = async (ids) => {
    const authService = getService('AuthenticationService');
    await authService.handleAuthenticationWithPostLoginAction('activate_fonts', {
      font_ids: ids
    });

    await syncVariations(ids);
  };

  showSyncSuccessMessage = (tkFontId) => {
    const i18n = getData('/neue/i18n');
    const firstMileService = getService('FirstMileService');
    firstMileService.onUseModelFontActivation(i18n, {
      queryParams: {
        tkFontId: tkFontId
      }
    });
  };

  showSyncErrorMessage = () => {
    const notificationService = getService('NotificationService');
    const i18n = getData('/neue/i18n');
    scopeApply(() => {
      notificationService.error(
        i18n['neue.browse.family_card']['error_occurred_adding_some_fonts'], {
          actionLabel: i18n['neue.browse.family_card']['try_again'],
          callbackAction: () => this.syncVariations()
        });
    });
  };

  unsyncVariations = async () => {
    await this.confirmUnsync();
    try {
      await this.unsyncVariationIds(this.ids);
      this.toolbar.synced = false;
      this.showUnsyncSuccessMessage();
      this.updateVariationCards(this.ids, false);
    } catch (e) {
      this.showUnsyncErrorMessage();
    }
  };

  unsyncVariationIds = async (ids) => {
    await unsyncVariations(ids);
  };

  confirmUnsync = async () => {
    return new Promise(resolve => {
      const firstMileService = getService('FirstMileService');
      firstMileService.shouldShowRemoveFamilyConfirmationDialog().then(shouldConfirm => {
        if (shouldConfirm) {
          const i18n = getData('/neue/i18n');
          const header = i18n['neue.browse.family_card']['remove_fonts_confirmation_header'];
          const description = i18n['neue.browse.family_card']['remove_fonts_confirmation_description'];
          firstMileService.showRemoveFamilyConfirmationDialog(header, description, resolve);
        } else {
          resolve();
        }
      });
    });
  };

  showUnsyncSuccessMessage = () => {
    const i18n = getData('/neue/i18n');
    const notificationService = getService('NotificationService');
    scopeApply(() => {
      notificationService.notice(i18n['neue.browse.family_card']['fonts_removed_success_message']);
    });
  };

  showUnsyncErrorMessage = () => {
    const i18n = getData('/neue/i18n');
    const notificationService = getService('NotificationService');

    scopeApply(() => notificationService.error(
      i18n['neue.browse.family_card']['error_occurred_deactivating_fonts']));
  };

  /**
   * Updates the variation card's sync state, so that it stays consistent
   * with the sync/unsync actions from the toolbar.
   *
   * @param {Array} ids Variation ids of cards that need updating
   * @param {boolean} synced State to update card to
   */
  updateVariationCards = (ids, synced) => {
    Array.from(this.element._variationCards)
      .filter(card => ids.includes(parseInt(card.dataset.id)))
      .map(card => card.querySelector('af-sync-button'))
      .forEach(syncButton => {
        syncButton.synced = synced;
      });
  };

  _getTkFontIds = (toolbar) => {
    return toolbar.libraryElements.map(libraryElement => libraryElement.typekitFontId);
  };

  _toFontVariationId = (typekitFontId) => {
    return parseInt((typekitFontId || '-').split('-')[1], 10) || 0;
  };
}

export function registerEventHandlers(element) {
  const librariesToolbarSync = new LibrariesToolbarSync(element);
  element.addEventListener(
    SYNC_STATE_CHANGE_EVENT, librariesToolbarSync.handleSyncStateChangeEvent);
}

